import { useEffect, useRef } from 'react';
import { useUser, isUserLoggedIn } from '~/modules/user';
import {
    consumeProductFilesModalReopenIntent,
    getCurrentProductFilesModalPageLoadID,
} from '../util/product-files-modal-reopen-intent';

const AUTH_LOGIN_INTENT_KEY = 'auth-login-intent';
const PRODUCT_FILES_AUTH_SOURCE = 'productFiles';

const isProductFilesAuthReturn = () => {
    if (typeof window === 'undefined') return false;

    return (
        window.sessionStorage.getItem(AUTH_LOGIN_INTENT_KEY) ===
        PRODUCT_FILES_AUTH_SOURCE
    );
};

interface UseProductFilesModalReopenOptions {
    /** The current product page ID. */
    productID: number;
    /** The current professional ID. */
    professionalID: number;
    /** Whether the page actually has downloadable files. */
    enabled: boolean;
    /** Callback that opens the files modal. */
    onReopen: () => void;
}

/**
 * Reopens the product files modal after an auth-triggered page reload.
 */
export const useProductFilesModalReopen = ({
    productID,
    professionalID,
    enabled,
    onReopen,
}: UseProductFilesModalReopenOptions) => {
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const onReopenRef = useRef(onReopen);
    const hasHandledAuthReloadRef = useRef(false);

    useEffect(() => {
        onReopenRef.current = onReopen;
    }, [onReopen]);

    useEffect(() => {
        if (hasHandledAuthReloadRef.current || !enabled || !isLoggedIn) return;
        if (!isProductFilesAuthReturn()) return;

        const { shouldReopen, shouldClear } =
            consumeProductFilesModalReopenIntent({
                productID,
                professionalID,
                pathname: window.location.pathname,
                pageLoadID: getCurrentProductFilesModalPageLoadID(),
            });
        if (!shouldClear) return;

        hasHandledAuthReloadRef.current = true;
        window.sessionStorage.removeItem(AUTH_LOGIN_INTENT_KEY);

        if (shouldReopen) {
            onReopenRef.current();
        }
    }, [enabled, isLoggedIn, productID, professionalID]);
};
